/**
 * Der Ausweg nach einem unbekannten Code (KONZEPT.md §8.3, ADR 0026) -- an
 * jeder Stelle, an der ein Gerät gescannt wird: Scannen, Mehrfachauswahl,
 * Ein- und Ausbuchen.
 *
 * Mit Netz: „Gerät anlegen“. Ohne Netz ist der Code schon gemerkt, bevor die
 * Meldung erscheint, und „Doch nicht merken“ nimmt ihn wieder heraus. Eine
 * Sackgasse gibt es nicht.
 */

import { useEffect, useMemo } from "react";
import { Link } from "react-router";
import { useOnline } from "../api/online.js";
import { useBestand, useLokal } from "../lokal/bestand.js";
import { alle, entfernen, erledigte, merken } from "../lokal/nachzuerfassen.js";
import { Knopf } from "./bausteine.jsx";

export interface Unbekannt {
  code: string;
  vorschlag: string | null;
  /** Der Behälter, in dem gescannt wurde. */
  ort_id: string | null;
  /** Ohne Netz gescannt und deshalb schon unter „Nachzuerfassen“. */
  gemerkt: boolean;
}

/** Beim Scan aufrufen: Ohne Netz wird der Code gleich gemerkt. */
export async function unbekanntErfassen(
  code: string,
  vorschlag: string | null,
  ort_id: string | null,
): Promise<Unbekannt> {
  const gemerkt = !globalThis.navigator.onLine;
  if (gemerkt) await merken(code, vorschlag, ort_id);
  return { code, vorschlag, ort_id, gemerkt };
}

/** Kopfzeile der Meldung. */
export function unbekanntKopf(u: Unbekannt): string {
  return u.gemerkt ? "Unbekannter Code – für später gemerkt" : "Dieser Code ist nicht zugeordnet";
}

/** Der Text unter der Kopfzeile, mit dem Ausweg. */
export function UnbekannterCodeText({
  unbekannt,
  aufAnlegen,
  aufVergessen,
}: {
  unbekannt: Unbekannt;
  aufAnlegen: () => void;
  aufVergessen: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p>
        Gelesen: <span className="font-mono">{unbekannt.code}</span>
        {unbekannt.vorschlag !== null && (
          <>
            <br />
            Das sieht aus wie die Inventarnummer{" "}
            <span className="font-mono font-bold">{unbekannt.vorschlag}</span>.
          </>
        )}
      </p>
      {unbekannt.gemerkt ? (
        <>
          <p>
            Mit Netz lässt er sich unter „Nachzuerfassen“ auf der Seite{" "}
            <Link to="/scannen" className="font-semibold underline">
              Scannen
            </Link>{" "}
            anlegen.
          </p>
          <div>
            <Knopf
              art="still"
              onClick={() => {
                void entfernen(unbekannt.code).then(aufVergessen);
              }}
            >
              Doch nicht merken
            </Knopf>
          </div>
        </>
      ) : (
        <div>
          <Knopf art="haupt" onClick={aufAnlegen}>
            Gerät anlegen
          </Knopf>
        </div>
      )}
    </div>
  );
}

/**
 * Die Liste „Nachzuerfassen“ -- auf der Seite „Scannen“, sobald etwas wartet.
 * Was die lokale Kopie inzwischen kennt, fällt von selbst heraus.
 */
export function Nachzuerfassen({ aufAnlegen }: { aufAnlegen: (u: Unbekannt) => void }) {
  const eintraege = useLokal(alle, []);
  const bestand = useBestand();
  const online = useOnline();

  const erledigt = useMemo(
    () => (eintraege === undefined || bestand === undefined ? [] : erledigte(bestand, eintraege)),
    [eintraege, bestand],
  );
  useEffect(() => {
    for (const e of erledigt) void entfernen(e.code);
  }, [erledigt]);

  const offen = (eintraege ?? []).filter((e) => !erledigt.includes(e));
  if (offen.length === 0) return null;

  return (
    <section
      aria-label="Nachzuerfassen"
      className="flex flex-col gap-2 rounded-xl bg-white p-4 dark:bg-neutral-800"
    >
      <h2 className="font-bold">Nachzuerfassen ({offen.length})</h2>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        Ohne Netz gescannt und unbekannt. Liegt nur auf diesem Gerät.
      </p>
      <ul className="flex flex-col">
        {offen.map((e) => {
          const ort = e.ort_id === null ? undefined : bestand?.lagerorte.get(e.ort_id);
          return (
            <li
              key={e.code}
              className="flex flex-col gap-2 border-b border-neutral-200 py-3 last:border-b-0 dark:border-neutral-700"
            >
              <span className="flex flex-col">
                <span className="font-mono font-bold">{e.vorschlag ?? e.code}</span>
                <span className="text-sm text-neutral-600 dark:text-neutral-300">
                  {new Date(e.erfasst_am).toLocaleString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {ort !== undefined && ` · gescannt in ${ort.kennung}`}
                </span>
              </span>
              <div className="grid grid-cols-2 gap-2">
                <Knopf
                  disabled={!online}
                  onClick={() => {
                    aufAnlegen({
                      code: e.code,
                      vorschlag: e.vorschlag,
                      ort_id: e.ort_id,
                      gemerkt: true,
                    });
                  }}
                >
                  {online ? "Anlegen" : "Anlegen – nur mit Netz"}
                </Knopf>
                <Knopf
                  art="still"
                  onClick={() => {
                    void entfernen(e.code);
                  }}
                >
                  Entfernen
                </Knopf>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
